import { realtimeV1 } from '@udm/contracts';
import type { ConnectionState } from './types';

/**
 * realtime/v1 client (docs/architecture/05-events-and-realtime.md §4.3):
 * connection states, exponential backoff with jitter, resume by epoch+seq, gap detection → resync.
 * Losing the socket never marks devices offline; it only changes the connection state shown in the UI.
 */
export interface WsClientOptions {
  url: string;
  clientVersion: string;
  onBatch: (events: realtimeV1.BatchEvent[]) => void;
  /** called when the server cannot resume: the owner re-fetches snapshots for active subscriptions only */
  onResyncRequired: (reason: string) => void;
  onState: (state: ConnectionState) => void;
  socketFactory?: (url: string) => WebSocket;
  random?: () => number;
}

type Sub = Extract<realtimeV1.ClientMessage, { t: 'sub' }>;

export class RealtimeClient {
  private ws: WebSocket | null = null;
  private epoch: string | null = null;
  private seq = 0;
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private stopped = false;
  private readonly subs = new Map<string, Sub>();
  private state: ConnectionState = 'offline';

  constructor(private readonly o: WsClientOptions) {}

  start(): void {
    this.stopped = false;
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    clearTimeout(this.timer);
    this.ws?.close(1000, 'client_stop');
    this.setState('offline');
  }

  subscribe(sub: Omit<Sub, 't' | 'level'> & { level?: Sub['level'] }): void {
    const msg: Sub = { t: 'sub', level: 'summary', ...sub };
    this.subs.set(sub.id, msg);
    this.sendRaw(msg);
  }

  unsubscribe(id: string): void {
    this.subs.delete(id);
    this.sendRaw({ t: 'unsub', id });
  }

  /** Browser came back online / tab became visible: reconnect now instead of waiting for backoff. */
  nudge(): void {
    if (this.state === 'reconnecting' || this.state === 'offline') {
      clearTimeout(this.timer);
      this.attempt = 0;
      this.connect();
    }
  }

  private setState(s: ConnectionState) {
    if (s !== this.state) {
      this.state = s;
      this.o.onState(s);
    }
  }

  private sendRaw(m: realtimeV1.ClientMessage) {
    if (this.ws?.readyState === 1) this.ws.send(JSON.stringify(m));
  }

  private connect(): void {
    if (this.stopped) return;
    this.setState(this.epoch ? 'reconnecting' : 'connecting');
    const ws = (this.o.socketFactory ?? ((u) => new WebSocket(u)))(this.o.url);
    this.ws = ws;
    ws.onopen = () => {
      this.sendRaw({
        t: 'hello',
        client_version: this.o.clientVersion,
        resume: this.epoch ? { epoch: this.epoch, seq: this.seq } : null,
      });
    };
    ws.onmessage = (ev) => this.onMessage(String(ev.data));
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (this.stopped) return;
      this.setState('reconnecting');
      const base = Math.min(30_000, 1000 * 2 ** this.attempt++);
      this.timer = setTimeout(() => this.connect(), base / 2 + (this.o.random ?? Math.random)() * (base / 2));
    };
  }

  private onMessage(raw: string) {
    let parsed: realtimeV1.ServerMessage;
    try {
      parsed = realtimeV1.ServerMessage.parse(JSON.parse(raw));
    } catch {
      return; // invalid server message: ignored (counted in dev tools later)
    }
    switch (parsed.t) {
      case 'welcome': {
        this.attempt = 0;
        const needsResync = this.epoch !== null && !parsed.resumed;
        this.epoch = parsed.epoch;
        this.seq = parsed.seq;
        for (const s of this.subs.values()) this.sendRaw(s);
        if (needsResync) {
          this.setState('resyncing');
          this.o.onResyncRequired('epoch_changed');
        }
        this.setState('live');
        break;
      }
      case 'batch':
        if (parsed.from !== this.seq + 1 && this.seq !== 0) {
          this.setState('resyncing');
          this.o.onResyncRequired('gap');
          this.setState('live');
        }
        this.seq = parsed.to;
        this.o.onBatch(parsed.events);
        break;
      case 'resync':
        this.setState('resyncing');
        this.o.onResyncRequired(parsed.reason);
        this.setState('live');
        break;
      case 'ping':
        this.sendRaw({ t: 'pong' });
        break;
      default:
        break;
    }
  }
}
